import * as THREE from 'three';
import { SwingInput } from './SwingInput';
import { SwingDebugOverlay } from './SwingDebugOverlay';
import type { ShotIntent } from './ShotIntent';

const AIM_PIXELS_TO_RADIANS = THREE.MathUtils.degToRad(0.18);
const KEYBOARD_AIM_RATE_RADIANS_PER_SECOND = THREE.MathUtils.degToRad(55);
const KEYBOARD_POWER_CHARGE_SECONDS = 1.6;

export function shouldIgnoreSwingPointerEvent(
  event: Pick<PointerEvent, 'pointerType' | 'defaultPrevented'>,
): boolean {
  return event.pointerType === 'mouse' || event.defaultPrevented;
}

export class InputManager {
  private suspended = false;
  private readonly swingInput: SwingInput;
  private readonly overlay: SwingDebugOverlay;
  private readonly pendingIntents: ShotIntent[] = [];
  private aimDeltaRadians = 0;
  private leftPressed = false;
  private rightPressed = false;
  private isChargingPower = false;
  private powerChargeSeconds = 0;
  private resetRequested = false;
  private clubDelta = 0;
  private slopeReadToggleRequested = false;
  private landingZoneToggleRequested = false;

  constructor(private readonly element: HTMLElement) {
    this.swingInput = new SwingInput(element);
    this.overlay = new SwingDebugOverlay(element);
    element.addEventListener('pointerdown', this.handlePointerDown, { passive: false });
    element.addEventListener('pointermove', this.handlePointerMove, { passive: false });
    element.addEventListener('pointerup', this.handlePointerUp, { passive: false });
    element.addEventListener('pointercancel', this.handlePointerCancel, { passive: false });
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    window.addEventListener('blur', this.handleWindowBlur);
  }

  update(deltaSeconds: number, elapsedSeconds: number, canAcceptShotInput: boolean): void {
    if (this.suspended) return;
    this.swingInput.update(elapsedSeconds);
    if (canAcceptShotInput) {
      const keyboardAimDirection = Number(this.rightPressed) - Number(this.leftPressed);
      this.aimDeltaRadians +=
        keyboardAimDirection * KEYBOARD_AIM_RATE_RADIANS_PER_SECOND * deltaSeconds;
      if (this.isChargingPower) {
        this.powerChargeSeconds = Math.min(
          KEYBOARD_POWER_CHARGE_SECONDS,
          this.powerChargeSeconds + deltaSeconds,
        );
      }
    }
    const intent = this.swingInput.consumeShotIntent();
    if (intent) this.pendingIntents.push(intent);
    this.aimDeltaRadians += this.swingInput.consumeAimDelta() * AIM_PIXELS_TO_RADIANS;
    this.overlay.update(this.swingInput.snapshot());
  }

  consumeShotIntent(): ShotIntent | null {
    return this.pendingIntents.shift() ?? null;
  }

  setSuspended(suspended: boolean): void {
    this.suspended = suspended;
    if (suspended) {
      this.pendingIntents.length = 0;
      this.handleWindowBlur();
    }
  }

  powerPercent(): number {
    return (this.powerChargeSeconds / KEYBOARD_POWER_CHARGE_SECONDS) * 100;
  }

  consumeClubDelta(): number {
    const delta = this.clubDelta;
    this.clubDelta = 0;
    return delta;
  }

  consumeResetRequested(): boolean {
    const requested = this.resetRequested;
    this.resetRequested = false;
    return requested;
  }

  consumeSlopeReadToggleRequested(): boolean {
    const requested = this.slopeReadToggleRequested;
    this.slopeReadToggleRequested = false;
    return requested;
  }

  consumeLandingZoneToggleRequested(): boolean {
    const requested = this.landingZoneToggleRequested;
    this.landingZoneToggleRequested = false;
    return requested;
  }

  consumeAimDeltaRadians(): number {
    const delta = this.aimDeltaRadians;
    this.aimDeltaRadians = 0;
    return delta;
  }

  dispose(): void {
    this.element.removeEventListener('pointerdown', this.handlePointerDown);
    this.element.removeEventListener('pointermove', this.handlePointerMove);
    this.element.removeEventListener('pointerup', this.handlePointerUp);
    this.element.removeEventListener('pointercancel', this.handlePointerCancel);
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('blur', this.handleWindowBlur);
    this.overlay.dispose();
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (this.suspended) return;
    if (event.repeat && event.code === 'Space') return;
    if (event.code === 'ArrowLeft' || event.code === 'KeyA') this.leftPressed = true;
    if (event.code === 'ArrowRight' || event.code === 'KeyD') this.rightPressed = true;
    if (event.code === 'Space') {
      event.preventDefault();
      this.isChargingPower = true;
    }
    if (event.code === 'KeyR') this.resetRequested = true;
    if (event.code === 'KeyG') this.slopeReadToggleRequested = true;
    if (event.code === 'KeyL') this.landingZoneToggleRequested = true;
    if (event.code === 'ArrowUp') this.clubDelta -= 1;
    if (event.code === 'ArrowDown') this.clubDelta += 1;
    if (event.code.startsWith('Digit')) {
      const digit = Number(event.code.replace('Digit', ''));
      if (digit >= 1 && digit <= 6) this.clubDelta = digit * 100;
    }
  };

  private handleKeyUp = (event: KeyboardEvent): void => {
    if (event.code === 'ArrowLeft' || event.code === 'KeyA') this.leftPressed = false;
    if (event.code === 'ArrowRight' || event.code === 'KeyD') this.rightPressed = false;
    if (event.code === 'Space') {
      event.preventDefault();
      if (this.isChargingPower) {
        this.pendingIntents.push({
          power: this.powerPercent(),
          tempo: 0,
          path: { samples: [], maxBackswingPixels: 0, impactPoint: null, straightness: 1 },
          faceAngle: 0,
          launchCommand: true,
        });
      }
      this.isChargingPower = false;
      this.powerChargeSeconds = 0;
    }
  };

  private handlePointerDown = (event: PointerEvent): void => {
    if (this.suspended) return;
    if (shouldIgnoreSwingPointerEvent(event)) return;
    event.preventDefault();
    this.swingInput.begin(event);
  };

  private handlePointerMove = (event: PointerEvent): void => {
    if (shouldIgnoreSwingPointerEvent(event)) return;
    event.preventDefault();
    this.swingInput.move(event);
  };

  private handlePointerUp = (event: PointerEvent): void => {
    if (shouldIgnoreSwingPointerEvent(event)) return;
    event.preventDefault();
    this.swingInput.end(event);
  };

  private handlePointerCancel = (event: PointerEvent): void => {
    if (shouldIgnoreSwingPointerEvent(event)) return;
    event.preventDefault();
    this.swingInput.cancel();
  };

  private handleWindowBlur = (): void => {
    this.isChargingPower = false;
    this.powerChargeSeconds = 0;
    this.swingInput.cancel();
  };
}
